import {ChevronRightIcon, MinusIcon, PlusIcon} from '@heroicons/react/solid';
import React, {useState} from 'react';
import {Link} from 'react-router-dom';

import {Button, Table, TBody, Td, Th, Thead, Tr} from '@/components';
import {useCountryList} from '@/context/CountryListContext';
import type {AdminAfriexPerson} from '@/types';
import {camelToTitle} from '@/utils/camelToTitle';
import {mapCountryCodeToFlags} from '@/utils/countries';
import {capitalizeWords} from '@/utils/dashboard';
import * as formatDate from '@/utils/date';

import KycSummary from './Summary';

type ExtraRowArg = AdminAfriexPerson;

const KycTable = ({
  items,
  extraRow,
}: {
  items: AdminAfriexPerson[];
  extraRow?: (user?: ExtraRowArg) => React.ReactNode;
}) => {
  return (
    <Table>
      <Thead>
        <Tr enableHover={false}>
          <Th className="w-20">&nbsp;</Th>
          <Th>Name</Th>
          <Th>Status</Th>
          <Th>Country</Th>
          <Th>Date</Th>
          <Th className="w-20">&nbsp;</Th>
        </Tr>
      </Thead>

      <TBody>
        {items.map((item, index) => (
          <KycTableRow
            key={index}
            item={item}
            extraRow={extraRow}
            index={index}
          />
        ))}
      </TBody>
    </Table>
  );
};

const KycTableRow = ({
  item,
  extraRow,
  index,
}: {
  item: AdminAfriexPerson;
  extraRow?: (user?: AdminAfriexPerson) => React.ReactNode;
  index: number;
}) => {
  const {state: countryListState} = useCountryList();
  const {countryList} = countryListState;
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const createdAt = new Date(item?.createdAt ?? '');
  const zebraRow = index % 2 === 0 ? '' : 'bg-gray-50 dark:bg-dark-zebraRow';

  return (
    <>
      <Tr className={zebraRow}>
        <Td isNumeric>
          <Button onClick={() => setIsExpanded(!isExpanded)} variant="unStyled">
            <div
              className={`border-2 border-gray-700 dark:border-dark-borderLight w-5 h-5 flex justify-center rounded hover:bg-cyan-50 ${
                isExpanded ? 'bg-cyan-50' : ''
              }`}
            >
              {isExpanded ? (
                <MinusIcon className="w-4 h-4 text-gray-700 dark:text-gray-100" />
              ) : (
                <PlusIcon className="w-4 h-4 text-gray-700 dark:text-gray-100" />
              )}
            </div>
          </Button>
        </Td>

        <Td className={`font-medium`}>{item.name?.fullName}</Td>

        <Td>{capitalizeWords(camelToTitle(item.kyc?.status || ''))}</Td>

        <Td>
          {item.currentCountry}{' '}
          {mapCountryCodeToFlags(countryList)[item.currentCountry]}
        </Td>

        <Td>{formatDate.DDMonYYYY(createdAt.toISOString())}</Td>

        <Td>
          <Link
            to={`/kyc/${item.id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <div className="w-5 h-5 flex justify-center text-gray-700  hover:border rounded-lg dark:text-gray-300 hover:border-gray-300 hover:bg-cyan-50 hover:text-cyan-500">
              <ChevronRightIcon className="w-5 h-5" />
            </div>
          </Link>
        </Td>
      </Tr>

      {isExpanded && (
        <Tr enableHover={false}>
          <Td colSpan={6} className="p-0">
            <div className="flex py-10 px-8 justify-center bg-sky-50 dark:bg-transparent w-full">
              <KycSummary
                user={item}
                extraRow={extraRow ? extraRow(item) : null}
              />
            </div>
          </Td>
        </Tr>
      )}
    </>
  );
};

export default KycTable;
